import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { useProjectDelete } from 'app/modules/wallforming/components/projects-manager/hooks/useProjectDelete';
import { useProjectLoad } from 'app/modules/wallforming/components/projects-manager/hooks/useProjectLoad';
import { useProjectSave } from 'app/modules/wallforming/components/projects-manager/hooks/useProjectSave';
import { useProjectsData } from 'app/modules/wallforming/components/projects-manager/hooks/useProjectsData';
import { useProjectsMenuEvents } from 'app/modules/wallforming/components/projects-manager/hooks/useProjectsMenuEvents';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { ProjectReference } from 'app/modules/wallforming/components/projects-manager/types/projectReference';

/**
 * Thin orchestrator that composes the project-manager sub-hooks.
 *
 * Responsibilities:
 *  - Handels panel-open state and the active-dialog setter (from the store).
 *  - Computes `isDirty` — a cross-cutting concern that depends on both
 *    `useProjectsData` (signature comparison) and `useProjectLoad`
 *    (pending load suppresses dirty during an in-flight load).
 *  - Provides `handleSaveAndLoadProject`, which sequences a save and a
 *    load and therefore needs both `useProjectSave` and `useProjectLoad`.
 *  - Wires the event-bus integration via `useProjectsMenuEvents`.
 *  - Returns a flat API that consumers (ProjectsManager) can destructure.
 */
export const useProjectsManager = () => {
    const { t } = useTranslation();

    const { isOpen, setIsOpen, activeDialog, setActiveDialog, setIsDirty } = usePlacementStore(
        useShallow((state) => ({
            isOpen: state.isProjectPanelOpen,
            setIsOpen: state.setIsProjectPanelOpen,
            activeDialog: state.activeProjectDialog,
            setActiveDialog: state.setActiveProjectDialog,
            setIsDirty: state.setIsDirty
        }))
    );

    const data = useProjectsData();
    const loadState = useProjectLoad({ activeSessionId: data.activeSessionId });

    const isDirty = useMemo(() => {
        if (!data.resolvedActiveProject || !data.lastSavedSignature || loadState.pendingLoadedProject) {
            return false;
        }
        return data.currentProjectSignature !== data.lastSavedSignature;
    }, [
        data.currentProjectSignature,
        data.lastSavedSignature,
        data.resolvedActiveProject,
        loadState.pendingLoadedProject
    ]);

    // Sync computed isDirty into the store so other components can subscribe directly
    useEffect(() => {
        setIsDirty(isDirty);
    }, [isDirty, setIsDirty]);

    // Save dialog state, executeSave, autosave timer, triggerSaveFlow
    const saveState = useProjectSave({
        resolvedActiveProject: data.resolvedActiveProject,
        isDirty,
        currentProjectSignature: data.currentProjectSignature,
        activeSessionId: data.activeSessionId,
        pendingLoadedProject: loadState.pendingLoadedProject
    });

    // Delete dialog state + handleDeleteProject
    const deleteState = useProjectDelete({ resolvedActiveProject: data.resolvedActiveProject });

    const { executeSave, openSaveDialog, triggerSaveFlow, handleSaveProject } = saveState;
    const { handleLoadProject, openLoadDialog } = loadState;

    const openProjectsModal = useCallback(() => setIsOpen(true), [setIsOpen]);

    useProjectsMenuEvents({ openProjectsModal, openSaveDialog, triggerSaveFlow });

    const handleSaveAndLoadProject = useCallback(
        async (project: ProjectReference) => {
            if (!data.resolvedActiveProject || !isDirty) {
                await handleLoadProject(project);
                return;
            }

            try {
                await executeSave({
                    projectId: data.resolvedActiveProject.id,
                    name: data.resolvedActiveProject.name,
                    description: data.resolvedActiveProject.description?.trim() ?? '',
                    isAutosave: false,
                    successMessage: t('infoSaveProjectSuccess', { projectName: data.resolvedActiveProject.name })
                });

                await handleLoadProject(project);
            } catch (error) {
                const message = error instanceof Error ? error.message : t('errorSaveProject');
                toastNotifications.error({ title: t('error'), message });
            }
        },
        [executeSave, handleLoadProject, isDirty, data.resolvedActiveProject, t]
    );

    return {
        // Panel state
        isOpen,
        setIsOpen,
        // Dialog state
        activeDialog,
        setActiveDialog,
        // Project data
        projects: data.projects,
        isProjectsPending: data.isProjectsPending,
        resolvedActiveProject: data.resolvedActiveProject,
        isDirty,
        // Save dialog state
        saveName: saveState.saveName,
        setSaveName: saveState.setSaveName,
        saveDescription: saveState.saveDescription,
        setSaveDescription: saveState.setSaveDescription,
        saveError: saveState.saveError,
        // Confirm dialogs
        loadCandidate: loadState.loadCandidate,
        setLoadCandidate: loadState.setLoadCandidate,
        deleteCandidate: deleteState.deleteCandidate,
        setDeleteCandidate: deleteState.setDeleteCandidate,
        // Mutation pending states
        isSavePending: saveState.isSavePending,
        isLoadPending: loadState.isLoadPending,
        isDeletePending: deleteState.isDeletePending,
        // Event handlers
        handleSaveProject,
        handleLoadProject,
        handleSaveAndLoadProject,
        handleDeleteProject: deleteState.handleDeleteProject,
        openSaveDialog,
        openLoadDialog,
        openDeleteDialog: deleteState.openDeleteDialog
    };
};
